// 浮動小数点型
import Decimal from 'decimal.js';
import { CurrencyNameJPY } from "../contexts/constants";
import type { Currency } from '../contexts/types';
import { hasDecimal } from './utils';

Decimal.set({ precision: 50 });

/**
 * 汎用的な通貨変換関数
 *
 * @param {number} amount - 元の金額
 * @param {string} sourceCurrency - 元の通貨 (例: "JPY"、"USD"など)
 * @param {string} targetCurrency - 変換先の通貨 (例: "USD"、"EUR"など)
 * @param {Currency[]} currencyData - 通貨データ配列
 * @returns {decimal} - 変換後の金額 (小数点以下2桁) または 0 (無効な入力時)
 */
export function convertCurrency(
  amount: number | Decimal,
  sourceCurrency: string,
  targetCurrency: string,
  currencyData: Currency[]
) {

  // 金額が null または undefined の場合のチェック
  if (amount === null || amount === undefined) {
    console.log("金額が指定されていません。");
    return new Decimal(0);
  }

  // 金額が数値として変換できない場合のチェック
  if (isNaN(Number(amount))) {
    console.error("金額が数値として無効です。");
    return new Decimal(0);
  }

  // 金額が0の場合は返却
  if (Number(amount) === 0) {
    return new Decimal(0);
  }

  if (!sourceCurrency || !targetCurrency || !currencyData || currencyData.length === 0) {
    console.error("通貨コード、または通貨データが正しくありません。");
    return new Decimal(0);
  }

  // 同一通貨の場合、変換せず元の金額を返す
  if (sourceCurrency === targetCurrency) {
    return new Decimal(amount);
  }

  // 日本円 (JPY) を仲介する場合の判定
  if (sourceCurrency === CurrencyNameJPY) {
    return convertYenToCurrency(amount, targetCurrency, currencyData);
  } else if (targetCurrency === CurrencyNameJPY) {
    return convertCurrencyToYen(amount, sourceCurrency, currencyData);
  } else {
    // 外貨 → 別の外貨
    return convertForeignCurrencyToForeignCurrency(amount, sourceCurrency, targetCurrency, currencyData);
  }
}

/**
 * 日本円から指定した外貨への変換
 * 
 * @param {number} yen - 日本円の金額
 * @param {string} targetCurrency - 変換先の通貨 (例: "USD", "EUR", etc.)
 * @param {Currency[]} currencyData - 通貨データ配列
 * @returns {decimal} - 変換後の金額 (小数点以下2桁) または 0 (無効な入力時)
 */
export function convertYenToCurrency(yen: number | Decimal, targetCurrency: string, currencyData: Currency[]) {
  if (!yen || !targetCurrency || !currencyData || currencyData.length === 0) {
    console.error("日本円、通貨コード、または通貨データが正しくありません。");
    return new Decimal(0);
  }

  const targetCurrencyData = currencyData.find(currency => currency.currencyName === targetCurrency);
  if (!targetCurrencyData) {
    console.error(`指定された通貨コード "${targetCurrency}" が見つかりません。`);
    return new Decimal(0);
  }

  // 日本円を指定した通貨に変換
  const exchangeRate = targetCurrencyData.exchangeRate;
  if (!exchangeRate) {
    console.error(`指定された為替レート "${exchangeRate}" が見つかりません。`);
    return new Decimal(0);
  }

  return new Decimal(yen).div(exchangeRate);
}

/**
 * 外貨から日本円への変換
 * 
 * @param {number} foreignAmount - 外貨の金額
 * @param {string} sourceCurrency - 外貨の通貨 (例: "USD", "EUR", etc.)
 * @param {Currency[]} currencyData - 通貨データ配列
 * @returns {decimal} - 変換後の日本円金額 (小数点以下2桁) または 0 (無効な入力時)
 */
export function convertCurrencyToYen(foreignAmount: number | Decimal, sourceCurrency: string, currencyData: Currency[]) {
  if (!foreignAmount || !sourceCurrency || !currencyData || currencyData.length === 0) {
    console.error("外貨金額、通貨コード、または通貨データが正しくありません。");
    return new Decimal(0);
  }

  const sourceCurrencyData = currencyData.find(currency => currency.currencyName === sourceCurrency);
  if (!sourceCurrencyData) {
    console.error(`指定された通貨コード "${sourceCurrency}" が見つかりません。`);
    return new Decimal(0);
  }

  // 外貨を日本円に変換
  const exchangeRate = sourceCurrencyData.exchangeRate;
  if (!exchangeRate) {
    console.error(`指定された為替レート "${exchangeRate}" が見つかりません。`);
    return new Decimal(0);
  }

  return new Decimal(foreignAmount).mul(exchangeRate);
}

/**
 * 外貨から別の外貨への変換（日本円を仲介）
 *
 * @param {number | Decimal} foreignAmount - 元の外貨の金額
 * @param {string} sourceCurrency - 元の外貨の通貨 (例: "USD")
 * @param {string} targetCurrency - 変換先の外貨の通貨 (例: "EUR")
 * @param {Currency[]} currencyData - 通貨データ配列
 * @returns {decimal} - 変換後の金額 (小数点以下2桁) または 0 (無効な入力時)
 */
export function convertForeignCurrencyToForeignCurrency(
  foreignAmount: number | Decimal,
  sourceCurrency: string,
  targetCurrency: string,
  currencyData: Currency[]
) {
  if (!foreignAmount || !sourceCurrency || !targetCurrency || !currencyData || currencyData.length === 0) {
    console.error("外貨金額、通貨コード、または通貨データが正しくありません。");
    return new Decimal(0);
  }

  const sourceCurrencyData = currencyData.find(currency => currency.currencyName === sourceCurrency);
  const targetCurrencyData = currencyData.find(currency => currency.currencyName === targetCurrency);

  if (!sourceCurrencyData || !targetCurrencyData) {
    console.error(`通貨コード "${sourceCurrency}" または "${targetCurrency}" が見つかりません。`);
    return new Decimal(0);
  }

  if (!sourceCurrencyData.exchangeRate) {
    console.error(`指定された為替レート(source) "${sourceCurrencyData.exchangeRate}" が見つかりません。`);
    return new Decimal(0);
  }
  if (!targetCurrencyData.exchangeRate) {
    console.error(`指定された為替レート(target) "${targetCurrencyData.exchangeRate}" が見つかりません。`);
    return new Decimal(0);
  }

  // 外貨を日本円に変換
  const yenEquivalent = new Decimal(foreignAmount).mul(sourceCurrencyData.exchangeRate);

  // 日本円を目的の外貨に変換
  return yenEquivalent.div(targetCurrencyData.exchangeRate);
}

/**
 * チャートデータセットを指定通貨に変換
 * @param chartData チャートデータ（datasets配列を持つオブジェクト）
 * @param sourceCurrency ソース通貨名
 * @param targetCurrency ターゲット通貨名
 * @param currencyData 通貨マスタデータ
 * @param decimalPlaces 小数点以下の桁数（省略時は通貨に応じて自動判定）
 * @param skipYAxisIds 変換をスキップするyAxisID（例: ["profitRate"]）
 * @returns 変換後のチャートデータ
 */
export function convertChartDatasets<T extends { datasets: any[] }>(
  chartData: T,
  sourceCurrency: string,
  targetCurrency: string,
  currencyData: Currency[],
  decimalPlaces?: number,
  skipYAxisIds?: string[]
): T {
  if (!chartData?.datasets) return chartData;
  const dp = decimalPlaces ?? (hasDecimal(targetCurrency) ? 2 : 0);
  return {
    ...chartData,
    datasets: chartData.datasets.map((dataset: any) => {
      if (skipYAxisIds?.includes(dataset.yAxisID)) return dataset;
      return {
        ...dataset,
        data: dataset.data.map((value: any) => {
          if (value === null || value === undefined) return value;
          if (sourceCurrency === targetCurrency) {
            return new Decimal(value).toDecimalPlaces(dp).toNumber();
          }
          return convertCurrency(new Decimal(value), sourceCurrency, targetCurrency, currencyData)
            .toDecimalPlaces(dp)
            .toNumber();
        })
      };
    })
  };
}
